import { z } from "zod"
import { ChannelEnum, FlowEnum } from "../campaigns/commonTypes"
import { CapabilityExecutionStatus } from "../capabilities/dtos"
import { MarketingChannelEnum } from "../customers/dtos"
import { ClusterDomainEnum, ClusterSegmentEnum } from "../insights/response_data"
import { LastContextSchema } from "../integrations/web/dtos"
import { SentimentEnum } from "../shared/common"
import { createFilterBySchema, DateRangeSchema, oneOrMany } from "../shared/filter_dtos"
import { PaginationSchema } from "../shared/pagination_dtos"
import { QuerySchema } from "../shared/query_dtos"
import { createSortSchema } from "../shared/sort_dtos"
import {
  ConversationStatusEnum,
  ConversationTestingExperimentOptionResponseDataSchema,
  ConversationTypeEnum,
  MessageFormatEnum,
  MessageRoleEnum,
} from "./response_data"

export const CONVERSATION_MESSAGE_MAX_FILE_BYTES = 50 * 1024 * 1024
export const CONVERSATION_MESSAGE_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const
export const CONVERSATION_MESSAGE_FILE_MIME_TYPES = [
  ...CONVERSATION_MESSAGE_IMAGE_MIME_TYPES,
  "application/pdf",
] as const

const ConversationCapabilityExecutionStatus = [
  CapabilityExecutionStatus.Success,
  CapabilityExecutionStatus.Unsuccess,
  CapabilityExecutionStatus.Error,
  CapabilityExecutionStatus.Skipped,
] as const

export const ToggleAISchema = z.object({
  aiDisabled: z.boolean(),
  permanent: z.boolean().optional(),
})

export const GetConversationsFilterBy = createFilterBySchema([
  {
    key: "customerId",
    value: z.string().optional(),
  } as const,
  {
    key: "campaignId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "flow",
    value: oneOrMany(z.enum(FlowEnum)),
  } as const,
  {
    key: "goalId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "goalOptionId",
    value: z.string().optional(),
  } as const,
  {
    key: "status",
    value: oneOrMany(z.union([z.enum(ConversationStatusEnum), z.literal("EngagedOrAchieved")])),
  } as const,
  {
    key: "type",
    value: z.enum(ConversationTypeEnum).optional(),
  } as const,
  {
    key: "isLive",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      })
      .optional(),
  } as const,
  {
    key: "hasPendingTicket",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      })
      .optional(),
  } as const,
  {
    key: "hasBusinessOpportunities",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      })
      .optional(),
  } as const,
  {
    key: "hasInternalNotes",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      })
      .optional(),
  } as const,
  {
    key: "noteMentionEmployeeId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "capabilityId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "capabilityExecutionStatus",
    value: z.enum(ConversationCapabilityExecutionStatus).optional(),
  } as const,
  {
    key: "hasAttachments",
    value: z
      .union([z.array(z.enum(MessageRoleEnum)), z.string(), z.enum(MessageRoleEnum)])
      .transform((val) => {
        if (typeof val === "string") {
          if (val === "User") return [MessageRoleEnum.User]
          if (val === "Assistant") return [MessageRoleEnum.Assistant]
          return [MessageRoleEnum.User, MessageRoleEnum.Assistant]
        }
        if (Array.isArray(val)) return val
        return [val]
      })
      .pipe(z.array(z.enum(MessageRoleEnum)))
      .optional(),
  } as const,
  {
    key: "hasSupport",
    value: z
      .union([
        z.array(z.enum(["questions", "tickets", "noTickets"])),
        z.string(),
        z.enum(["questions", "tickets", "noTickets"]),
      ])
      .transform((val) => {
        if (typeof val === "string") {
          return [val as "questions" | "tickets" | "noTickets"]
        }
        if (Array.isArray(val)) return val
        return [val]
      })
      .pipe(z.array(z.enum(["questions", "tickets", "noTickets"])))
      .optional(),
  } as const,
  {
    key: "channel",
    value: oneOrMany(z.enum(ChannelEnum)),
  } as const,
  {
    key: "domain",
    value: z.enum(ClusterDomainEnum).optional(),
  } as const,
  {
    key: "canonicalId",
    value: z.string().optional(),
  } as const,
  {
    key: "snapshot",
    value: z.string().optional(),
  } as const,
  {
    key: "segmentId",
    value: oneOrMany(z.string()).transform((segmentIds) =>
      segmentIds?.filter((segmentId) => segmentId !== ClusterSegmentEnum.all),
    ),
  } as const,
  {
    key: "tagIds",
    value: z.array(z.string().regex(/^\d+$/)).optional(),
  } as const,
  {
    key: "subscribedTo",
    value: oneOrMany(z.enum(MarketingChannelEnum)),
  } as const,
  {
    key: "sentiment",
    value: oneOrMany(z.enum(SentimentEnum)),
  } as const,
  {
    key: "dateRange",
    value: DateRangeSchema,
  } as const,
])

export const GetConversationsSortFields = [
  "status",
  "createdAt",
  "customer.lastMessageSentAt",
] as const
export const GetConversationsSortBy = createSortSchema(
  GetConversationsSortFields,
  "createdAt",
  "desc",
)

export const GetConversationsSchema = PaginationSchema.extend(GetConversationsFilterBy.shape)
  .extend(GetConversationsSortBy.shape)
  .extend(QuerySchema.shape)
  .extend(
    z.object({
      includeExperiments: z
        .union([z.boolean(), z.string()])
        .transform((value) => {
          if (typeof value === "string") return value === "true"
          return value
        })
        .optional(),
    }).shape,
  )

export const GetConversationSchema = z.object({
  id: z.string().transform(BigInt),
})

export const ExportPromptEvaluationConversationSnapshotQuerySchema = z.object({
  lastMessageId: z.string().transform(BigInt),
})

export const GetConversationTimelineQuerySchema = z
  .object({
    beforeMessageId: z.string().transform(BigInt).optional(),
    afterMessageId: z.string().transform(BigInt).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    includeRaw: z
      .union([z.boolean(), z.enum(["true", "false"])])
      .transform((value) => (typeof value === "string" ? value === "true" : value))
      .default(false),
  })
  .refine((value) => !(value.beforeMessageId && value.afterMessageId), {
    message: "beforeMessageId and afterMessageId are mutually exclusive",
    path: ["afterMessageId"],
  })

export const GetConversationsMessagesQuerySchema = z.object({
  campaignId: z.string().transform(BigInt).optional(),
})

export const GetWebConversationSchema = z.object({
  id: z.string(),
})

export const ConversationIdSchema = z.object({
  conversationId: z.string().transform(BigInt),
})

export const ConversationTestingExperimentSchema =
  ConversationTestingExperimentOptionResponseDataSchema

export const CreateTestingConversationSchema = z.object({
  customerId: z.string().transform(BigInt),
})

export const CreateTestingConversationParamsSchema = z.object({
  campaignId: z.string().transform(BigInt),
  experiment: ConversationTestingExperimentSchema.nullable().optional(),
})

const ConversationMessageFileSchema = z.object({
  data: z.unknown().refine((value) => Buffer.isBuffer(value), {
    error: "Expected a Buffer",
  }),
  name: z.string(),
  mimetype: z.string(),
})

export const ConversationUploadedImageSchema = z.object({
  mediaUrl: z.string(),
  format: z.literal(MessageFormatEnum.Image),
})

export const ConversationUploadedFileSchema = z.object({
  mediaUrl: z.url(),
  format: z.enum([MessageFormatEnum.Image, MessageFormatEnum.Document]),
})

export const ConversationUploadedFilesResponseDataSchema = z.array(ConversationUploadedFileSchema)

export const ConversationUploadedImagesResponseDataSchema = z.array(ConversationUploadedImageSchema)

export const CreateConversationMessageBodySchema = z.object({
  content: z.string().optional(),
  uploadedImages: z.array(ConversationUploadedImageSchema).optional(),
  uploadedFiles: z.array(ConversationUploadedFileSchema).optional(),
})

export const SendWhatsappTemplateMessageBodySchema = z.object({
  templateId: z.string().transform(BigInt),
})

export const CreateConversationMessageImageFilesSchema = z.object({
  file: z.union([ConversationMessageFileSchema, z.array(ConversationMessageFileSchema)]),
})

const ConversationMessageUploadFileSchema = ConversationMessageFileSchema.extend({
  size: z.number().int().nonnegative(),
  truncated: z.boolean(),
})

export const CreateConversationMessageFilesSchema = z.object({
  file: z.union([
    ConversationMessageUploadFileSchema,
    z.array(ConversationMessageUploadFileSchema),
  ]),
})

export const EnhanceMessageBodySchema = z.object({
  content: z.string(),
})

export const WebConversationBodySchema = z.object({
  content: z.string().optional(),
  lastContext: LastContextSchema.optional(),
})

export const WebConversationFilesSchema = z.object({
  file: ConversationMessageFileSchema,
})

export const GetConversationGoalsSchema = z.object({
  conversationId: z.string().transform(BigInt),
})

export const GetConversationsResponsesFilterBy = createFilterBySchema([
  {
    key: "campaignId",
    value: z.string().optional(),
  } as const,
  {
    key: "goalId",
    value: z.string().optional(),
  } as const,
  {
    key: "customerId",
    value: z.string().optional(),
  } as const,
  {
    key: "avoidUnderlying",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => {
        if (typeof value === "string") return value === "true"
        return value
      })
      .optional(),
  } as const,
  {
    key: "canonicalId",
    value: z.string().optional(),
  } as const,
  {
    key: "channel",
    value: z.enum(ChannelEnum).optional(),
  } as const,
  {
    key: "conversationFlow",
    value: z.enum(FlowEnum).optional(),
  } as const,
])

export const GetConversationsResponsesSortFields = ["createdAt"] as const
export const GetConversationsResponsesSortBy = createSortSchema(
  GetConversationsResponsesSortFields,
  "createdAt",
  "desc",
)

export const GetConversationsResponsesSchema = GetConversationsResponsesFilterBy.extend(
  GetConversationsResponsesSortBy.shape,
)
  .extend(QuerySchema.shape)
  .extend(PaginationSchema.shape)

export const GetConversationOpportunitiesFilterBy = createFilterBySchema([
  {
    key: "customerId",
    value: z.string().optional(),
  } as const,
  {
    key: "campaignId",
    value: z.string().optional(),
  } as const,
  {
    key: "conversationId",
    value: z.string().optional(),
  } as const,
  {
    key: "dateRange",
    value: DateRangeSchema,
  } as const,
])

export const GetConversationOpportunitiesSortFields = ["createdAt"] as const
export const GetConversationOpportunitiesSortBy = createSortSchema(
  GetConversationOpportunitiesSortFields,
  "createdAt",
  "desc",
)

export const GetConversationOpportunitiesSchema = PaginationSchema.extend(
  GetConversationOpportunitiesFilterBy.shape,
)
  .extend(GetConversationOpportunitiesSortBy.shape)
  .extend(QuerySchema.shape)
